import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import NavigationUtils from 'Utils/NavigationUtils';
import SagaUtils from 'Utils/SagaUtils';
import ApiService from '../../../Services/ApiService';
import { displayNotification } from '../../../Utils/GenericUtils';
import { BillingActions } from '../billing/actions';
import { OrganizationActions } from './actions';
import { OrganizationSelectors } from './selectors';

export function* fetchOrganizationDetails() {
  try {
    const organizationDetails = yield call(ApiService.getOrganizationDetails);
    yield put(OrganizationActions.organizationDetailsFetchSucceeded(organizationDetails));
  } catch (e) {
    yield put(OrganizationActions.organizationDetailsFetchFailed(e.response.data));
    displayNotification('error', 'Error', 'Failed retrieving Organization Details');
  }
}

export function* fetchOrganizationIPWhitelist() {
  try {
    const organizationIPWhitelist = yield call(ApiService.getOrganizationIPWhitelist);
    yield put(OrganizationActions.ipWhitelistFetchSucceeded(organizationIPWhitelist));
  } catch (e) {
    yield put(OrganizationActions.ipWhitelistFetchFailed(e.response.data));
    if (e.response.status >= 500) {
      displayNotification('error', 'Error', 'Failed retrieving Organization IP Whitelist');
    }
  }
}

export function* editDetailsHandled(action) {
  try {
    const orgData = action.payload;
    const shouldGetRedirected = yield select(OrganizationSelectors.shouldRedirectToCreditCardSelector);

    const newOrganizationDetails = yield call(ApiService.changeOrganizationDetails, orgData);

    yield put(OrganizationActions.editDetailsHandlerSucceeded(newOrganizationDetails));
    window.scrollTo(0, 0);
    displayNotification('success', 'Success', 'Your organization details were updated successfully');

    if (shouldGetRedirected) {
      yield put(BillingActions.setBillingEditMode(orgData));
      yield put(OrganizationActions.resetRedirect(orgData));
      NavigationUtils.push(ApiService.generateLink('billing/'));
    }
  } catch (e) {
    yield put(OrganizationActions.editDetailsHandlerFailed(e.response.data));
    displayNotification('error', 'Error', e.response.data.address);
  }
}

export function* editOrgDetailsHandled(action) {
  try {
    const orgData = action.payload;
    const newOrganizationDetails = yield call(ApiService.changeOrganizationDetails, orgData);

    yield put(OrganizationActions.editDetailsHandlerSucceeded(newOrganizationDetails));
  } catch (e) {
    yield put(OrganizationActions.editDetailsHandlerFailed(e.response.data));
    displayNotification('error', 'Error', e.response.data.address);
  }
}

export function* setOrgDetailsOnboarding(action) {
  try {
    const orgData = action.payload;
    const newOrganizationDetails = yield call(ApiService.setOrganizationDetailsOnboarding, orgData);

    yield put(OrganizationActions.setDetailsOnboardingSucceeded(newOrganizationDetails));
  } catch (e) {
    yield put(OrganizationActions.setDetailsOnboardingFailed(e.response.data));
    displayNotification('error', 'Error', e.response.data.address);
  }
}

export function* toggleGoogleLogin() {
  try {
    const forcedGoogleLoginStatus = yield call(ApiService.toggleGoogleLogin);

    if (forcedGoogleLoginStatus) {
      yield put(OrganizationActions.forceGoogleLoginEnableSucceeded(forcedGoogleLoginStatus));
      displayNotification('success', 'Success', 'Google login will be mandatory');
    } else {
      yield put(OrganizationActions.forceGoogleLoginDisableSucceeded(forcedGoogleLoginStatus));
      displayNotification('success', 'Success', 'Google login will no longer be mandatory');
    }
  } catch (e) {
    yield put(OrganizationActions.forceGoogleLoginDisableFailed(e.response.data));
    displayNotification('error', 'Error', 'Something went wrong... try again later');
  }
}

export function* toggleAmazonLogin() {
  try {
    const forcedLoginStatus = yield call(ApiService.toggleAmazonLogin);

    if (forcedLoginStatus) {
      yield put(OrganizationActions.forceAmazonLoginEnableSucceeded(forcedLoginStatus));
      displayNotification('success', 'Success', 'Amazon login will be mandatory');
    } else {
      yield put(OrganizationActions.forceAmazonLoginDisableSucceeded(forcedLoginStatus));
      displayNotification('success', 'Success', 'Amazon login will no longer be mandatory');
    }
  } catch (e) {
    yield put(OrganizationActions.forceAmazonLoginDisableFailed(e.response.data));
    displayNotification('error', 'Error', 'Something went wrong... try again later');
  }
}

export function* toggleOidcLogin() {
  try {
    const forcedLoginStatus = yield call(ApiService.toggleOidcLogin);

    if (forcedLoginStatus) {
      yield put(OrganizationActions.forceOidcLoginEnableSucceeded(forcedLoginStatus));
      displayNotification('success', 'Success', 'Organization login will be mandatory');
    } else {
      yield put(OrganizationActions.forceOidcLoginDisableSucceeded(forcedLoginStatus));
      displayNotification('success', 'Success', 'Organization login will no longer be mandatory');
    }
  } catch (e) {
    yield put(OrganizationActions.forceOidcLoginDisableFailed(e.response.data));
    displayNotification('error', 'Error', 'Something went wrong... try again later');
  }
}

function* load() {
  yield put(OrganizationActions.organizationDetailsFetch());
}

export function* watchOrganization() {
  yield SagaUtils.allIfOrgAdmin([
    takeEvery(OrganizationActions.organizationDetailsFetch, fetchOrganizationDetails),
    takeEvery(OrganizationActions.editDetailsHandler, editDetailsHandled),
    takeEvery(OrganizationActions.setDetailsOnboarding, setOrgDetailsOnboarding),
    takeEvery(OrganizationActions.editOrgDetailsHandler, editOrgDetailsHandled),
    takeEvery(OrganizationActions.forceGoogleLoginEnable, toggleGoogleLogin),
    takeEvery(OrganizationActions.forceAmazonLoginEnable, toggleAmazonLogin),
    takeEvery(OrganizationActions.forceGoogleLoginDisable, toggleGoogleLogin),
    takeEvery(OrganizationActions.forceAmazonLoginDisable, toggleAmazonLogin),
    takeEvery(OrganizationActions.forceOidcLoginEnable, toggleOidcLogin),
    takeEvery(OrganizationActions.forceOidcLoginDisable, toggleOidcLogin),
    takeEvery(OrganizationActions.ipWhitelistFetch, fetchOrganizationIPWhitelist),
    fork(load),
  ]);
}
